// AI Citator analytics helpers. One helper per event; observability-only, so
// they no-op silently when `trackEvent` is unavailable. Events are case-agnostic
// by design (see analytics/README.md).

import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent, type AnalyticsEventProperties } from '@/analytics/schema/events.schema'
import { CitatorBadgeType, CitatorOpenedIn, CitatorPanelSource, CitatorSurface, CitatorTreatmentOutcome } from '@/analytics/enums/citator.enums'

type WarnFn = (message: string, context?: unknown) => void

type CitatorBaseContext = {
  trackEvent: TrackEventFn | undefined
  surface: CitatorSurface
  warn?: WarnFn
}

// Shared guard + emit so each public helper is just a params → payload mapping.
function emitCitatorEvent<E extends AnalyticsEvent>(
  trackEvent: TrackEventFn | undefined,
  event: E,
  payload: AnalyticsEventProperties[E] & Record<string, string | number | boolean>,
  warn?: WarnFn
): AnalyticsEventProperties[E] | null {
  if (!trackEvent) {
    warn?.(`[Citator Analytics] trackEvent not available when attempting to track ${event}`)
    return null
  }
  trackEvent(event, payload)
  return payload
}

type TrackCitatorBadgeImpressionParams = CitatorBaseContext & {
  badgeType: CitatorBadgeType
}

export function trackCitatorBadgeImpression(params: TrackCitatorBadgeImpressionParams) {
  const { trackEvent, surface, badgeType, warn } = params
  return emitCitatorEvent(trackEvent, AnalyticsEvent.CitatorBadgeImpression, { citatorBadgeType: badgeType, citatorSurface: surface }, warn)
}

type TrackCitatorBadgeClickedParams = TrackCitatorBadgeImpressionParams

export function trackCitatorBadgeClicked(params: TrackCitatorBadgeClickedParams) {
  const { trackEvent, surface, badgeType, warn } = params
  return emitCitatorEvent(trackEvent, AnalyticsEvent.CitatorBadgeClicked, { citatorBadgeType: badgeType, citatorSurface: surface }, warn)
}

type TrackCitatorPanelOpenedParams = CitatorBaseContext & {
  source: CitatorPanelSource
  /** Defaults to the in-page content viewer. */
  openedIn?: CitatorOpenedIn
}

// "Panel" is the historical event name; the semantic is "the citator detail view was
// reached" — `citatorOpenedIn` says through which component (the in-page content
// viewer, or the standalone viewer deep-linked to Citation Analysis). Callers must
// not fire it for opens that land on the source view.
export function trackCitatorPanelOpened(params: TrackCitatorPanelOpenedParams) {
  const { trackEvent, surface, source, openedIn = CitatorOpenedIn.ContentViewer, warn } = params
  return emitCitatorEvent(
    trackEvent,
    AnalyticsEvent.CitatorPanelOpened,
    { citatorPanelSource: source, citatorSurface: surface, citatorOpenedIn: openedIn },
    warn
  )
}

type TrackCitatorTreatmentOutcomeClickedParams = CitatorBaseContext & {
  outcome: CitatorTreatmentOutcome
}

export function trackCitatorTreatmentOutcomeClicked(params: TrackCitatorTreatmentOutcomeClickedParams) {
  const { trackEvent, surface, outcome, warn } = params
  return emitCitatorEvent(trackEvent, AnalyticsEvent.CitatorTreatmentOutcomeClicked, { citatorTreatmentOutcome: outcome, citatorSurface: surface }, warn)
}

type TrackCitatorReferenceGroupExpandedParams = CitatorBaseContext & {
  containsBadges: boolean
}

export function trackCitatorReferenceGroupExpanded(params: TrackCitatorReferenceGroupExpandedParams) {
  const { trackEvent, surface, containsBadges, warn } = params
  return emitCitatorEvent(trackEvent, AnalyticsEvent.CitatorReferenceGroupExpanded, { citatorSurface: surface, citatorContainsBadges: containsBadges }, warn)
}
